import { useState, useEffect } from 'react';
import { paymentsAPI } from '../../api';
import { Button } from '../ui/Button';

/**
 * Payment history modal – read-only viewer for payments on an invoice.
 * Displays: payment date, amount, method (manual/stripe/sumup), bank account, reference, notes.
 *
 * @param {string} invoiceId - Invoice UUID
 * @param {string} invoiceNum - Invoice number (for modal title)
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when user closes the modal
 */
const PaymentHistoryModal = ({ invoiceId, invoiceNum, open, onClose }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && invoiceId) {
      fetchPayments();
    } else {
      setPayments([]);
      setError('');
    }
  }, [open, invoiceId]);

  const fetchPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await paymentsAPI.getAll({ invoice_id: invoiceId });
      const list = res?.payments ?? (Array.isArray(res) ? res : []);
      setPayments(list);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError(err.message || 'Failed to load payment history');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatMethod = (method) => {
    if (!method) return '—';
    const m = String(method).toLowerCase();
    if (m === 'manual') return 'Manual';
    if (m === 'stripe') return 'Stripe';
    if (m === 'sumup') return 'SumUp';
    return method;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Payment History – Invoice {invoiceNum || '—'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl transition-colors leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              Loading payments...
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No payments recorded for this invoice.
            </div>
          ) : (
            <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                      Date
                    </th>
                    <th className="text-right px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                      Amount
                    </th>
                    <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                      Method
                    </th>
                    <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                      Bank Account
                    </th>
                    <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                      Reference
                    </th>
                    <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-gray-200 dark:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-700/30"
                    >
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatDate(p.payment_date)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 tabular-nums font-medium">
                        {formatCurrency(p.amount)}
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                        {formatMethod(p.payment_method)}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                        {p.account_name ?? p.bank_account_name ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[120px] truncate">
                        {p.reference || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[150px] truncate">
                        {p.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;
